import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ToggleRight, Search, Sparkles, GraduationCap, Inbox, Check } from "lucide-react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { Input } from "../../components/ui/input";

// Etichette leggibili + descrizione breve dei moduli
const MODULE_LABELS = {
  presenze:   { name: "Presenze e HR",         desc: "Timbrature, ferie, permessi, malattia" },
  ticket:     { name: "Ticket e supporto",     desc: "Ticket cliente ↔ Wattline con allegati" },
  messaggi:   { name: "Messaggi interni",      desc: "Chat privata tra colleghi dell'azienda" },
  documenti:  { name: "Documenti",             desc: "Archivio documentale aziendale" },
  formazione: { name: "Formazione",            desc: "Catalogo corsi SCORM con progressi tracciati" },
  onboarding: { name: "Scuola onboarding",     desc: "Percorsi di ingresso per nuove risorse" },
  checklist:  { name: "Checklist e scadenze",  desc: "Task ricorrenti e reminders" },
  scadenze:   { name: "Scadenze aziendali",    desc: "Alert su rinnovi, contratti, adempimenti" },
  verifica:   { name: "Verifica dati aziendali", desc: "VIES, CCIAA, Catasto (OpenAPI.it)" },
  ai_tools:   { name: "Assistente AI",         desc: "Riconcilia file, chiedi al tuo file" },
};

export default function TenantFeatures() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState({}); // key = `${org_id}:${module}` → bool while saving
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [moduleRequests, setModuleRequests] = useState([]);
  const [handlingReq, setHandlingReq] = useState(null);

  const loadRequests = async () => {
    try {
      const { data } = await api.get("/superadmin/module-requests");
      setModuleRequests(data || []);
    } catch { /* sezione opzionale */ }
  };
  useEffect(() => { loadRequests(); }, []);

  const markHandled = async (req) => {
    setHandlingReq(req.id);
    try {
      await api.patch(`/superadmin/module-requests/${req.id}`, { status: "handled" });
      setModuleRequests((rs) => rs.map((r) => r.id === req.id ? { ...r, status: "handled" } : r));
      toast.success(`Richiesta di ${req.org_name} segnata come gestita`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setHandlingReq(null);
    }
  };

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/tenant-features");
      setRows(data.rows || []);
      setModules(data.modules || []);
      setFailed(false);
    } catch (e) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (org, moduleName) => {
    const newValue = !org.features[moduleName];
    const key = `${org.org_id}:${moduleName}`;
    // Optimistic update
    setPending((p) => ({ ...p, [key]: true }));
    setRows((rs) => rs.map((r) => r.org_id === org.org_id
      ? { ...r, features: { ...r.features, [moduleName]: newValue } }
      : r
    ));
    try {
      await api.patch(`/superadmin/tenant-features/${org.org_id}`, {
        features: { [moduleName]: newValue },
      });
      toast.success(
        newValue
          ? `Modulo «${MODULE_LABELS[moduleName]?.name || moduleName}» attivato per ${org.org_name}`
          : `Modulo «${MODULE_LABELS[moduleName]?.name || moduleName}» disattivato per ${org.org_name}`
      );
    } catch (e) {
      // Rollback su errore
      setRows((rs) => rs.map((r) => r.org_id === org.org_id
        ? { ...r, features: { ...r.features, [moduleName]: !newValue } }
        : r
      ));
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setPending((p) => { const c = { ...p }; delete c[key]; return c; });
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) =>
      r.org_name.toLowerCase().includes(q) || r.org_id.toLowerCase().includes(q)
    );
  }, [rows, query]);

  return (
    <div className="wl-page">
      <div className="wl-page__header">
        <div className="wl-page__eyebrow">
          <ToggleRight size={12} strokeWidth={2} style={{marginRight: 4, verticalAlign: "-1px"}} />
          Amministrazione Wattline
        </div>
        <h1 className="wl-page__title">Funzioni per cliente</h1>
        <p className="wl-page__meta">
          Attiva o disattiva i moduli per singolo tenant. Le voci nascoste in
          sidebar seguono questa configurazione. Gli endpoint dei moduli spenti
          restituiscono 403.
        </p>
      </div>

      {moduleRequests.length > 0 && (
        <div className="mb-6 bg-surface border border-border rounded-xl" data-testid="module-requests-section">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Inbox size={15} style={{color: "var(--wl-text-secondary)"}} />
            <span className="text-sm font-medium" style={{color: "var(--wl-ink)"}}>Richieste moduli</span>
            <span className="wl-badge wl-badge--warning" style={{fontSize: "10px"}}>
              {moduleRequests.filter((r) => r.status === "new").length} nuove
            </span>
          </div>
          <div className="divide-y" style={{borderColor: "var(--wl-border)"}}>
            {moduleRequests.slice(0, 15).map((r) => (
              <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
                   data-testid={`module-request-row-${r.id}`}>
                <div className="text-sm min-w-0">
                  <span className="font-medium" style={{color: "var(--wl-ink)"}}>{r.org_name}</span>
                  <span style={{color: "var(--wl-text-secondary)"}}> · {MODULE_LABELS[r.module]?.name || r.module}</span>
                  <div className="text-[11px]" style={{color: "var(--wl-text-muted)"}}>
                    {r.user_name} ({r.user_email}) · {new Date(r.created_at).toLocaleString("it-IT", {dateStyle: "short", timeStyle: "short"})}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {r.status === "new" ? (
                    <>
                      <span className="wl-badge wl-badge--warning" style={{fontSize: "10px"}}>nuova</span>
                      <button type="button" onClick={() => markHandled(r)} disabled={handlingReq === r.id}
                              className="wl-btn wl-btn--ghost wl-btn--sm" style={{border: "1px solid var(--wl-border)"}}
                              data-testid={`module-request-handle-${r.id}`}>
                        {handlingReq === r.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Segna gestita
                      </button>
                    </>
                  ) : (
                    <span className="wl-badge wl-badge--success" style={{fontSize: "10px"}}>gestita</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-md mb-4 relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{color: "var(--wl-text-muted)"}} />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtra per nome o id tenant…"
          className="wl-input"
          style={{paddingLeft: "36px"}}
          data-testid="tenant-features-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm" style={{color: "var(--wl-text-secondary)"}}>
          <Loader2 size={14} className="animate-spin" /> Carico configurazione…
        </div>
      ) : failed ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox what="la configurazione dei moduli" onRetry={load} testId="tenant-features-error" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-8 text-center text-sm" style={{color: "var(--wl-text-secondary)"}}>
          Nessuna organizzazione trovata.
        </div>
      ) : (
        <div className="overflow-x-auto bg-surface border border-border rounded-xl">
          <table className="w-full text-sm" data-testid="tenant-features-table">
            <thead>
              <tr className="border-b border-border" style={{background: "var(--wl-card-header-bg)"}}>
                <th className="text-left px-4 py-3 font-medium sticky left-0 z-10" style={{color: "var(--wl-text-secondary)", background: "var(--wl-card-header-bg)"}}>
                  Tenant
                </th>
                {modules.map((m) => (
                  <th key={m} className="text-center px-3 py-3 font-medium min-w-[110px]" style={{color: "var(--wl-text-secondary)"}}>
                    <div className="text-[11px] uppercase tracking-wide">{MODULE_LABELS[m]?.name || m}</div>
                    <div className="text-[10px] font-normal mt-0.5" style={{color: "var(--wl-text-muted)"}}>
                      {MODULE_LABELS[m]?.desc || ""}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.org_id} className="border-b border-border last:border-b-0 hover:bg-page transition-colors" data-testid={`tenant-row-${row.org_id}`}>
                  <td className="px-4 py-3 sticky left-0 z-10" style={{background: "var(--wl-surface)"}}>
                    <div className="font-medium" style={{color: "var(--wl-ink)"}}>{row.org_name}</div>
                    <div className="text-[11px] mt-0.5 flex items-center gap-1.5" style={{color: "var(--wl-text-muted)"}}>
                      <span>{row.org_id}</span>
                      {row.org_id === "wattline-demo" && (
                        <span className="wl-badge wl-badge--warning" style={{fontSize: "10px"}}>
                          <Sparkles size={9} strokeWidth={2} /> demo
                        </span>
                      )}
                    </div>
                    {row.features.formazione && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span
                          className={`wl-badge ${row.scorm_uploaded ? "wl-badge--success" : "wl-badge--neutral"}`}
                          style={{fontSize: "10px"}}
                          data-testid={`scorm-status-${row.org_id}`}
                        >
                          {row.n_courses > 0
                            ? `${row.n_courses} ${row.n_courses === 1 ? "corso assegnato" : "corsi assegnati"}`
                            : "Nessun corso"}
                        </span>
                        <Link
                          to="/superadmin/formazione"
                          className="wl-btn wl-btn--ghost wl-btn--sm"
                          style={{border: "1px solid var(--wl-border)"}}
                          data-testid={`ff-formazione-url-${row.org_id}`}
                        >
                          <GraduationCap size={12} />
                          Catalogo corsi
                        </Link>
                      </div>
                    )}
                  </td>
                  {modules.map((m) => {
                    const key = `${row.org_id}:${m}`;
                    const enabled = !!row.features[m];
                    const busy = !!pending[key];
                    return (
                      <td key={m} className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => !busy && toggle(row, m)}
                          disabled={busy}
                          data-testid={`ff-toggle-${row.org_id}-${m}`}
                          aria-pressed={enabled}
                          aria-label={`${enabled ? "Disattiva" : "Attiva"} ${MODULE_LABELS[m]?.name || m} per ${row.org_name}`}
                          className="wl-toggle"
                          data-enabled={enabled ? "true" : "false"}
                        >
                          <span className="wl-toggle__thumb" aria-hidden="true" />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
